// src/context/UserRightsContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { FC } from '../constants/functionCodes';
import { isHarmonateUser as isServerHarmonateUser } from '../auth/serverIdentity';

const UserRightsContext = createContext();

export const useUserRights = () => {
  const context = useContext(UserRightsContext);
  if (!context) {
    throw new Error('useUserRights must be used within a UserRightsProvider');
  }
  return context;
};

export const UserRightsProvider = ({ children }) => {
  const [userRights, setUserRights] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rightsLoaded, setRightsLoaded] = useState(false);

  // Use AuthContext for authentication state
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  
  // Get user info from localStorage (more reliable than context state)
  const isHarmonateUser = isServerHarmonateUser();
  const userId = localStorage.getItem('user_id');

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // SIMPLIFIED: Fetch user rights immediately when user ID is available
  const fetchUserRights = async (targetUserId = null) => {
    const effectiveUserId = targetUserId || userId;
    
    if (!effectiveUserId) {
      console.log('No user ID available for fetching rights');
      return;
    }

    if (isHarmonateUser) {
      console.log('Harmonate user detected, setting empty rights');
      setUserRights({});
      setRightsLoaded(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Fetching user rights for user:', effectiveUserId);

      // Try different endpoints for user rights
      const endpoints = [
        `${API_BASE_URL}conductor/user-rights/${effectiveUserId}`,
        `${API_BASE_URL}conductor/user-rights`,
        `${API_BASE_URL}conductor/roles/rights/user/${effectiveUserId}`
      ];

      let response = null;
      let workingEndpoint = null;

      for (const endpoint of endpoints) {
        try {
          console.log('Trying endpoint:', endpoint);
          
          response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            workingEndpoint = endpoint;
            console.log('Found working endpoint:', endpoint);
            break;
          } else {
            console.log('Endpoint failed:', endpoint, 'Status:', response.status);
          }
        } catch (err) {
          console.log('Endpoint error:', endpoint, err.message);
        }
      }

      if (!workingEndpoint) {
        console.error('All endpoints failed for user rights');
        setUserRights({});
        setRightsLoaded(true);
        return;
      }

      console.log('User rights API response status:', response.status, response.statusText);

      const data = await response.json();
      console.log('User rights API raw response:', data);
      
      // Get current tenant ID from localStorage
      const currentTenantId = localStorage.getItem('tenant_id');
      console.log('Current tenant ID:', currentTenantId);
      
      // Transform the rights data into a more usable format
      const rightsMap = {};
      
      console.log('Raw API response data:', data);
      
      // Handle the API response with tenant_rights array
      if (data && data.tenant_rights && Array.isArray(data.tenant_rights)) {
        console.log('Processing tenant_rights array with', data.tenant_rights.length, 'items');
        
        // Find the current tenant's rights
        const currentTenantRights = data.tenant_rights.find(
          tenant => tenant.tenant_id === currentTenantId
        );
        
        if (currentTenantRights && currentTenantRights.user_rights) {
          console.log(`Found rights for tenant ${currentTenantId} with ${currentTenantRights.user_rights.length} functions`);
          
          currentTenantRights.user_rights.forEach(right => {
            const functionCode = right.function_code || right.functionCode;
            if (functionCode) {
              rightsMap[functionCode] = {
                view: Boolean(right.view),
                add: Boolean(right.add),
                edit: Boolean(right.edit),
                upload: Boolean(right.upload),
                enable: Boolean(right.enable),
                delete: Boolean(right.delete),
                export: Boolean(right.export)
              };
            }
          });
        } else {
          console.warn(`No rights found for current tenant (${currentTenantId})`);
        }
      } 
      // Fallback for other response formats
      else if (data && Array.isArray(data)) {
        console.log('Processing array data with', data.length, 'items');
        data.forEach(right => {
          const functionCode = right.function_code || right.functionCode;
          if (functionCode) {
            rightsMap[functionCode] = {
              view: Boolean(right.view),
              add: Boolean(right.add),
              edit: Boolean(right.edit),
              upload: Boolean(right.upload),
              enable: Boolean(right.enable),
              delete: Boolean(right.delete),
              export: Boolean(right.export)
            };
          }
        });
      } else if (data && data.user_rights && Array.isArray(data.user_rights)) {
        // Handle the alternative structure with user_rights array
        console.log('Processing user_rights array with', data.user_rights.length, 'items');
                 data.user_rights.forEach(right => {
           const { function_code, view, add, edit, upload, enable, delete: deletePerm, export: exportPerm } = right;
           if (function_code) {
             rightsMap[function_code] = {
               view: Boolean(view),
               add: Boolean(add),
               edit: Boolean(edit),
               upload: Boolean(upload),
               enable: Boolean(enable),
               delete: Boolean(deletePerm),
               export: Boolean(exportPerm)
             };
           }
         });
      } else {
        console.warn('Unexpected user rights data structure:', data);
        console.warn('Expected: Array of objects with function_code, view, add, edit, upload, enable');
      }

      console.log('Transformed user rights:', rightsMap);
      
      // Set the rights and mark as loaded
      setUserRights(rightsMap);
      setRightsLoaded(true);
      
      console.log('=== USER RIGHTS SET SUCCESSFULLY ===');
      console.log('Available functions:', Object.keys(rightsMap));
      console.log('Total rights count:', Object.keys(rightsMap).length);
      
    } catch (err) {
      console.error('Error fetching user rights:', err);
      setError(err.message);
      setUserRights({});
      setRightsLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has specific permission for a function
  const hasPermission = (functionCode, action) => {
    // Harmonate users have all permissions
    if (isHarmonateUser) {
      return true;
    }

    // If rights not loaded yet, return false to be safe
    if (!rightsLoaded) {
      console.log(`Rights not loaded yet for function: ${functionCode}`);
      return false;
    }

    // Check if function exists in user rights
    const functionRights = userRights[functionCode];
    if (!functionRights) {
      console.log(`No rights found for function: ${functionCode}`);
      return false;
    }

    // Check specific action
    const hasAccess = Boolean(functionRights[action]);
    console.log(`Permission check - Function: ${functionCode}, Action: ${action}, Access: ${hasAccess}`);
    return hasAccess;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions) => {
    if (isHarmonateUser) {
      return true;
    }

    if (!Array.isArray(permissions)) {
      return false;
    }

    return permissions.some(permission => {
      const { functionCode, action } = permission;
      return hasPermission(functionCode, action);
    });
  };

  // Check if a function is enabled for the user
  const isFunctionEnabled = (functionCode) => {
    if (isHarmonateUser) {
      return true;
    }

    const functionRights = userRights[functionCode];
    if (!functionRights) {
      return false;
    }
    
    const isEnabled = Boolean(functionRights.enable);
    console.log(`Function enabled check - Function: ${functionCode}, Enabled: ${isEnabled}`);
    return isEnabled;
  };

  // Get all permissions for a function
  const getFunctionPermissions = (functionCode) => {
    if (isHarmonateUser) {
      return {
        view: true,
        add: true,
        edit: true,
        upload: true,
        enable: true,
        delete: true,
        export: true
      };
    }

    return userRights[functionCode] || {
      view: false,
      add: false,
      edit: false,
      upload: false,
      enable: false,
      delete: false,
      export: false
    };
  };

  // Get all available functions for the user
  const getAvailableFunctions = () => {
    if (isHarmonateUser) {
      return [];
    }

    return Object.keys(userRights).filter(functionCode => {
      const rights = userRights[functionCode];
      return rights && (rights.view || rights.add || rights.edit || rights.upload || rights.enable || rights.delete || rights.export);
    });
  };

  // SIMPLIFIED: Check if user can see a menu item
  const canAccessMenu = (functionCode) => {
    console.log(`canAccessMenu called for: ${functionCode}`, {
      isHarmonateUser,
      rightsLoaded,
      functionRights: userRights[functionCode],
      availableFunctions: Object.keys(userRights)
    });

    if (isHarmonateUser) {
      console.log(`Harmonate user - granting access to ${functionCode}`);
      return true;
    }

    // If rights not loaded yet, deny access to be safe
    if (!rightsLoaded) {
      console.log(`Rights not loaded yet - denying access to ${functionCode}`);
      return false;
    }

    const functionRights = userRights[functionCode];
    if (!functionRights) {
      // Home stays available until the Home role row exists for this user.
      if (functionCode === FC.HOME) {
        return true;
      }
      console.log(`No rights found for function: ${functionCode}`);
      return false;
    }

    // User can access menu only if the function is enabled
    const hasAccess = Boolean(functionRights.enable);
    console.log(`Menu access check - Function: ${functionCode}, Enable: ${functionRights.enable}, Final: ${hasAccess}`);
    return hasAccess;
  };

  // NEW: Check if user can access a specific function endpoint (for API calls)
  const canAccessFunction = (functionCode) => {
    console.log(`canAccessFunction called for: ${functionCode}`, {
      isHarmonateUser,
      rightsLoaded,
      functionRights: userRights[functionCode]
    });

    if (isHarmonateUser) {
      console.log(`Harmonate user - granting access to function: ${functionCode}`);
      return true;
    }

    // If rights not loaded yet, deny access to be safe
    if (!rightsLoaded) {
      console.log(`Rights not loaded yet - denying access to function: ${functionCode}`);
      return false;
    }

    const functionRights = userRights[functionCode];
    if (!functionRights) {
      console.log(`No rights found for function: ${functionCode}`);
      return false;
    }

    // Check if user has view permission
    const hasViewAccess = Boolean(functionRights.view || functionRights.enable);
    console.log(`Function access check - ${functionCode}:`, { 
      view: functionRights.view, 
      enable: functionRights.enable, 
      hasViewAccess 
    });

    return hasViewAccess;
  };

  // Refresh user rights (useful after role changes)
  const refreshUserRights = () => {
    console.log('Manual refresh of user rights triggered');
    fetchUserRights();
  };

  // Manual trigger for debugging
  const forceFetchUserRights = () => {
    console.log('Force fetching user rights for debugging');
    if (userId && !isHarmonateUser) {
      fetchUserRights();
    } else {
      console.log('Cannot force fetch - userId:', userId, 'isHarmonateUser:', isHarmonateUser);
    }
  };

  // Debug function that can be called from console
  const debugUserRights = () => {
    console.log('=== UserRights Debug Info ===');
    console.log('Current state:', {
      authLoading,
      isAuthenticated,
      userId,
      isHarmonateUser,
      userRights: Object.keys(userRights),
      rightsLoaded,
      loading,
      error
    });
    console.log('localStorage items:', {
      token: !!localStorage.getItem('token'),
      user_id: localStorage.getItem('user_id'),
      harmonate_user: String(isServerHarmonateUser()),
      isLoggedIn: localStorage.getItem('isLoggedIn')
    });
    console.log('===========================');
  };

  // Make debug function globally available
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugUserRights = debugUserRights;
      window.forceFetchUserRights = forceFetchUserRights;
      window.manualFetchUserRights = () => {
        console.log('=== Manual fetch triggered from console ===');
        const currentUserId = localStorage.getItem('user_id');
        const currentIsHarmonate = isServerHarmonateUser();
        console.log('Current localStorage state:', { currentUserId, currentIsHarmonate });
        
        if (currentUserId && !currentIsHarmonate) {
          console.log('Manually fetching user rights for:', currentUserId);
          fetchUserRights(currentUserId);
        } else {
          console.log('Cannot manually fetch - userId:', currentUserId, 'isHarmonate:', currentIsHarmonate);
        }
      };
      
      window.testUserRightsFlow = async () => {
        console.log('=== Testing Complete User Rights Flow ===');
        const currentUserId = localStorage.getItem('user_id');
        const currentIsHarmonate = isServerHarmonateUser();
        const token = localStorage.getItem('token');
        
        console.log('Current state:', { currentUserId, currentIsHarmonate, hasToken: !!token });
        
        if (!currentUserId || currentIsHarmonate || !token) {
          console.log('Cannot test - missing required data');
          return;
        }
        
        // Test the API call directly
        try {
          console.log('Testing API call to:', `${API_BASE_URL}conductor/user-rights/${currentUserId}`);
          const response = await fetch(`${API_BASE_URL}conductor/user-rights/${currentUserId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          console.log('API Response status:', response.status);
          const data = await response.json();
          console.log('API Response data:', data);
          console.log('Data type:', typeof data, 'Is array:', Array.isArray(data));
          
          if (Array.isArray(data)) {
            console.log('Data is array with', data.length, 'items');
            data.forEach((item, index) => {
              console.log(`Item ${index}:`, item);
            });
          }
        } catch (error) {
          console.error('API test failed:', error);
        }
      };
      
      window.forceSetUserRights = (rightsData) => {
        console.log('=== Force Setting User Rights ===');
        console.log('Input rights data:', rightsData);
        
        if (rightsData && typeof rightsData === 'object') {
          setUserRights(rightsData);
          setRightsLoaded(true);
          console.log('User rights force-set to:', rightsData);
          console.log('Available functions:', Object.keys(rightsData));
        } else {
          console.error('Invalid rights data provided');
        }
      };
      
      window.testNewApproach = () => {
        console.log('=== Testing New Simplified Approach ===');
        console.log('Current state:', {
          userId: localStorage.getItem('user_id'),
          isHarmonate: String(isServerHarmonateUser()),
          token: !!localStorage.getItem('token'),
          rightsLoaded,
          userRightsCount: Object.keys(userRights).length,
          availableFunctions: Object.keys(userRights)
        });
        
        if (rightsLoaded) {
          console.log('✅ Rights are loaded and ready');
          console.log('Available functions:', Object.keys(userRights));
          
          // Test specific functions
          const testFunctions = ['Workflow', 'GL Sync', 'User Management'];
          testFunctions.forEach(func => {
            const canAccess = canAccessMenu(func);
            const canAccessFunc = canAccessFunction(func);
            console.log(`${func}: Menu: ${canAccess}, Function: ${canAccessFunc}`);
          });
        } else {
          console.log('❌ Rights not loaded yet');
          console.log('This means the user rights API call has not completed');
        }
      };
      
      console.log('Debug functions available:');
      console.log('- window.debugUserRights() - show current state');
      console.log('- window.forceFetchUserRights() - force fetch with current userId');
      console.log('- window.manualFetchUserRights() - manual fetch with localStorage userId');
      console.log('- window.testUserRightsFlow() - test complete flow');
      console.log('- window.forceSetUserRights(data) - manually set user rights');
      console.log('- window.testNewApproach() - test the new simplified approach');
    }
  }, [debugUserRights, forceFetchUserRights, fetchUserRights]);

  // Reset rights (useful on logout)
  const resetUserRights = () => {
    setUserRights({});
    setRightsLoaded(false);
    setLoading(false);
    setError(null);
  };

  // SIMPLIFIED: Main effect - fetch rights when user ID is available
  useEffect(() => {
    console.log('=== UserRights useEffect triggered ===');
    console.log('Current state:', { 
      authLoading, 
      isAuthenticated, 
      userId, 
      isHarmonateUser,
      rightsLoaded,
      currentUserRightsCount: Object.keys(userRights).length
    });
    
    // Don't fetch if already loaded
    if (rightsLoaded) {
      console.log('Rights already loaded, skipping fetch');
      return;
    }
    
    // Don't fetch if auth is still loading
    if (authLoading) {
      console.log('Auth still loading, skipping user rights fetch');
      return;
    }
    
    // Fetch rights if we have a user ID
    if (userId) {
      console.log('User ID available, fetching user rights');
      fetchUserRights();
    } else {
      console.log('No user ID available yet');
    }
    
    console.log('=== UserRights useEffect completed ===');
  }, [userId, authLoading, rightsLoaded]);

  // Listen for login success events
  useEffect(() => {
    const handleLoginSuccess = () => {
      console.log('Login success event received, checking for user rights fetch...');
      // Small delay to ensure localStorage is updated
      setTimeout(() => {
        const newUserId = localStorage.getItem('user_id');
        const newIsHarmonate = isServerHarmonateUser();
        
        console.log('Post-login check:', { newUserId, newIsHarmonate });
        
        if (newUserId && !newIsHarmonate && !rightsLoaded) {
          console.log('Triggering user rights fetch from login event');
          fetchUserRights(newUserId);
        }
      }, 500);
    };

    const handleUserRightsFetch = (event) => {
      console.log('User rights fetch event received:', event.detail);
      const { userId: eventUserId, isHarmonate } = event.detail;
      
      if (eventUserId && !isHarmonate && !rightsLoaded) {
        console.log('Immediately fetching user rights for:', eventUserId);
        fetchUserRights(eventUserId);
      }
    };

    window.addEventListener('login-success', handleLoginSuccess);
    window.addEventListener('user-rights-fetch', handleUserRightsFetch);
    
    return () => {
      window.removeEventListener('login-success', handleLoginSuccess);
      window.removeEventListener('user-rights-fetch', handleUserRightsFetch);
    };
  }, [rightsLoaded]);

  // Listen for logout events to reset rights
  useEffect(() => {
    const handleLogout = () => {
      resetUserRights();
    };

    window.addEventListener('logout-success', handleLogout);
    return () => window.removeEventListener('logout-success', handleLogout);
  }, []);

  const value = {
    userRights,
    loading,
    error,
    rightsLoaded, // New: indicates if rights have been loaded
    hasPermission,
    hasAnyPermission,
    isFunctionEnabled,
    getFunctionPermissions,
    getAvailableFunctions,
    canAccessMenu,
    canAccessFunction, // New: for checking function access before API calls
    refreshUserRights,
    forceFetchUserRights,
    fetchUserRights,
    debugUserRights,
    resetUserRights,
    isHarmonateUser
  };

  return (
    <UserRightsContext.Provider value={value}>
      {children}
    </UserRightsContext.Provider>
  );
};